import { Link } from "@tanstack/react-router";
import { Heart } from "lucide-react";
import { EditorialImage } from "./EditorialImage";
import { Stars } from "./ProductReviews";
import { formatDH, useShop } from "@/lib/shop-store";
import { useAllReviewStats } from "@/lib/reviews";
import type { Product } from "@/lib/catalog";
import { cn } from "@/lib/utils";

export function ProductCard({ product, className }: { product: Product; className?: string }) {
  const { toggleWishlist, isWished } = useShop();
  const { data: allStats } = useAllReviewStats();
  const stats = allStats?.[product.slug];
  const wished = isWished(product.id);


  return (
    <article className={cn("group relative", className)}>
      {/* Cadre à ratio fixe : la hauteur est réservée avant le chargement de l'image */}
      <Link
        to="/produit/$slug"
        params={{ slug: product.slug }}
        aria-label={product.name}
        className="relative block aspect-[4/5] overflow-hidden bg-sand"
      >
        <EditorialImage
          src={product.image}
          alt={product.name}
          ratio="product"
          label={`${product.name} — photo produit`}
          className="absolute inset-0 h-full w-full"
          sizes="(min-width: 1024px) 25vw, 50vw"
        />
        <div className="card-overlay" aria-hidden="true" />
      </Link>

      {product.isNew ? (
        <span className="card-badge label-xs absolute left-3 top-3 text-[0.5rem] tracking-[0.22em] sm:text-[0.55rem]">
          <span
            className="card-badge-dot motion-safe:animate-[pulse_2.8s_cubic-bezier(0.4,0,0.6,1)_infinite]"
            aria-hidden="true"
          />
          Nouveau
        </span>
      ) : null}

      <button
        type="button"
        aria-label={wished ? "Retirer des favoris" : "Ajouter aux favoris"}
        aria-pressed={wished}
        onClick={() => toggleWishlist(product.id)}
        className="absolute right-3 top-3 grid h-8 w-8 place-items-center bg-background/80 backdrop-blur-sm transition-colors hover:bg-background"
      >
        <Heart
          size={15}
          strokeWidth={1.3}
          className={cn("transition-colors", wished ? "fill-accent text-accent" : "text-foreground")}
        />
      </button>

      {/* Bloc texte à hauteur fixe pour éviter tout décalage au rendu du nom, prix et catégorie */}
      <div className="mt-4 flex h-16 items-start justify-between gap-3">
        <div className="flex h-full min-w-0 flex-col justify-between">
          <Link to="/produit/$slug" params={{ slug: product.slug }}>
            <h3 className="line-clamp-2 font-serif text-lg leading-snug">{product.name}</h3>
          </Link>
          <p className="label-xs h-4 text-[0.55rem] leading-4 text-muted-foreground">{product.category}</p>
        </div>
        <p className="shrink-0 pt-0.5 text-sm tabular-nums">{formatDH(product.price)}</p>
      </div>

      {/* Hauteur réservée en permanence : les cartes d'une même ligne restent alignées */}
      <div className="mt-1 flex h-5 items-center gap-2 text-xs text-muted-foreground">
        {stats && stats.count > 0 ? (
          <>
            <Stars value={stats.average} size={12} />
            <span className="tabular-nums">{stats.average.toFixed(1).replace(".", ",")}</span>
            <span>({stats.count} avis)</span>
          </>
        ) : null}
      </div>




    </article>
  );
}
